import React, { useState, useCallback, useEffect } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { adminAPI } from '@/services/api';
import { User, Customer } from '@/types';
import { ApproveUserDialog } from '@/components/users/management/common/dialogs/ApproveUserDialog';
import { CustomerCreateForm } from '@/components/users/customer/superadmin/forms/CustomerCreateForm';
import { usePlans, useMainDomain, useCustomerActions } from '@/hooks/customers';
import { calculateMaxUsers } from '@/utils/customerUtils';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';
import AlertDialog from '@/components/users/management/common/dialogs/AlertDialog';
import { PendingUsersFilters } from '../admin/tabs/PendingUsersTab/components/PendingUsersFilters';
import { PendingUsersTable } from '../admin/tabs/PendingUsersTab/components/PendingUsersTable';
import { CustomerAssignDialog } from '../admin/tabs/PendingUsersTab/components/CustomerAssignDialog';
import { PAGINATION, PLAN_LIMITS } from '@/constants';
import { useSearchFilters } from '@/hooks/common';
import { parseCustomerId, stringifyCustomerId, filterUsers, handleAPIError } from '@/utils';

interface PendingUsersTabProps {
  onEditUser: (user: User) => void;
  onApproveUser: (userId: number, customerId?: number, companyName?: string) => Promise<void>;
  onCreateCustomer?: (user: User) => void;
  // SaaS 관리자 전용: 고객사 생성/연결 기능 노출 여부 (기업관리자는 false로 전달)
  showCreateCustomer?: boolean;
  onRefresh?: () => void;  // 외부에서 리스트 새로고침을 트리거할 수 있는 콜백
  // Admin 모드: 고정된 customerId 사용
  fixedCustomerId?: number | null; // 자신의 고객사 ID (Admin 모드)
  // Superadmin 모드: 고객사 필터 사용
  initialCustomerId?: number | null; // 필터링할 고객사 ID (null이면 전체)
  customers?: Array<{ id: number; customer_name: string; customer_code: string }>; // 고객사 목록 (Superadmin 모드용)
  approvingUserId?: number | null;  // 승인 진행 중인 사용자 ID
  // UI 옵션
  showCustomerFilter?: boolean;  // 고객사 필터 표시 여부
  useBackendFiltering?: boolean;  // 백엔드 필터링 사용 여부 (Superadmin 모드)
}

const PendingUsersTab: React.FC<PendingUsersTabProps> = ({
  onEditUser,
  onApproveUser,
  onCreateCustomer,
  showCreateCustomer = true,
  onRefresh,
  fixedCustomerId,
  initialCustomerId,
  customers: propCustomers,
  approvingUserId,
  showCustomerFilter = false,
  useBackendFiltering = false,
}) => {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = PAGINATION.USER_MANAGEMENT_PAGE_SIZE;
  
  // Admin 모드: 고정된 customerId 사용
  const isAdminMode = fixedCustomerId !== undefined;
  const effectiveCustomerId = isAdminMode ? fixedCustomerId : null;
  
  // Superadmin 모드: URL과 동기화되는 필터 상태
  const {
    searchQuery: [searchQuery, setSearchQuery],
    status: [statusFilter, setStatusFilter],
    selectedCustomerIdStr: [selectedCustomerIdStr, setSelectedCustomerIdStr],
    resetFilters: resetPendingFilters,
  } = useSearchFilters('pending', {
    searchQuery: '',
    status: 'all',
    selectedCustomerIdStr: showCustomerFilter ? 'none' : '',
  });
  
  // Superadmin 모드: 문자열을 number | null | undefined로 변환
  const selectedCustomerId = showCustomerFilter ? parseCustomerId(selectedCustomerIdStr as string) : null;
  const setSelectedCustomerId = (value: number | null | undefined) => {
    if (showCustomerFilter) {
      setSelectedCustomerIdStr(stringifyCustomerId(value));
    }
  };

  // initialCustomerId가 변경되면 selectedCustomerId 업데이트 (Superadmin 모드)
  useEffect(() => {
    if (showCustomerFilter && initialCustomerId !== undefined && initialCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(initialCustomerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCustomerId, showCustomerFilter]);

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvingUser, setApprovingUser] = useState<User | null>(null);
  const customers = propCustomers || [];
  
  // 고객사 생성 관련 상태
  const [showCreateCustomerDialog, setShowCreateCustomerDialog] = useState(false);
  const [userForCustomerCreation, setUserForCustomerCreation] = useState<User | null>(null);
  const [newCustomer, setNewCustomer] = useState<{
    customer_code: string;
    alias: string;
    customer_name: string;
    max_users: number;
    service_url: string;
    contract_end_date: string;
    plan_id: number;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    notes: string;
  }>({
    customer_code: '',
    alias: '',
    customer_name: '',
    max_users: PLAN_LIMITS.PROFESSIONAL.default,
    service_url: '',
    contract_end_date: '',
    plan_id: 1,
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: ''
  });
  
  // 고객사 할당 관련 상태
  const [showAssignCustomerDialog, setShowAssignCustomerDialog] = useState(false);
  const [userForCustomerAssignment, setUserForCustomerAssignment] = useState<User | null>(null);
  
  // 고객사 생성에 필요한 데이터
  const plans = usePlans();
  const mainDomain = useMainDomain();
  const customerActions = useCustomerActions(() => {
    // onSuccess 콜백 (필요시 사용)
  });
  
  // 모달 상태
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [confirmCancelInviteOpen, setConfirmCancelInviteOpen] = useState(false);
  const [userToReject, setUserToReject] = useState<User | null>(null);
  const [userToCancelInvite, setUserToCancelInvite] = useState<User | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');

  const fetchPendingUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = (page - 1) * pageSize;
      
      if (useBackendFiltering) {
        // Superadmin 모드: 백엔드 필터링
        const customerIdForAPI = selectedCustomerId === undefined ? undefined : (selectedCustomerId === null ? 0 : selectedCustomerId);
        const statusFilterForAPI = statusFilter === 'all' ? undefined : statusFilter;
        const response = await adminAPI.getPendingUsers(
          skip,
          pageSize,
          customerIdForAPI,
          statusFilterForAPI,
          searchQuery || undefined
        );
        setPendingUsers(response.users || []);
        setTotal(response.total || 0);
      } else {
        // Admin 모드: 클라이언트 사이드 필터링
        const response = await adminAPI.getPendingUsers(skip, pageSize);
        const users = response.users || [];
        setPendingUsers(users);
        setTotal(response.total || 0);
      }
    } catch (error: any) {
      handleAPIError(error, '승인대기중 목록을 불러오는데 실패했습니다.', setError);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, useBackendFiltering, selectedCustomerId, statusFilter, searchQuery]);

  // 초기 로드 및 페이지 변경 시 검색
  useEffect(() => {
    fetchPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  
  // 필터 변경 시 첫 페이지로 이동하고 검색 (Superadmin 모드만)
  useEffect(() => {
    if (useBackendFiltering) {
      if (page === 1) {
        fetchPendingUsers();
      } else {
        setPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, statusFilter, searchQuery, useBackendFiltering]);

  // 고객사 생성 핸들러
  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userForCustomerCreation) return;
    
    const plan = plans.find(p => p.id === newCustomer.plan_id);
    const planName = plan?.plan_name.toLowerCase();
    const maxUsers = calculateMaxUsers(planName, newCustomer.max_users);
    
    const customerData: any = {
      ...newCustomer,
      max_users: maxUsers
    };
    
    delete customerData.service_url;
    
    if (!customerData.customer_code) customerData.customer_code = undefined;
    if (!customerData.alias) customerData.alias = undefined;
    if (!customerData.contact_name) customerData.contact_name = undefined;
    if (!customerData.contact_email) customerData.contact_email = undefined;
    if (!customerData.contact_phone) customerData.contact_phone = undefined;
    if (!customerData.notes) customerData.notes = undefined;
    
    try {
      const createdCustomer = await customerActions.createCustomer(customerData);
      // 고객사 생성 후 사용자에게 할당하고 승인
      await onApproveUser(userForCustomerCreation.id, createdCustomer.id);
      setShowCreateCustomerDialog(false);
      setUserForCustomerCreation(null);
      setNewCustomer({
        customer_code: '',
        alias: '',
        customer_name: '',
        max_users: PLAN_LIMITS.PROFESSIONAL.default,
        service_url: '',
        contract_end_date: '',
        plan_id: 1,
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        notes: ''
      });
      if (onRefresh) {
        onRefresh();
      }
      await fetchPendingUsers();
    } catch (error) {
      // Error is handled by hook
    }
  };
  
  // 고객사 할당 핸들러
  const handleAssignCustomer = async (customerId: number) => {
    if (!userForCustomerAssignment) return;
    
    try {
      await onApproveUser(userForCustomerAssignment.id, customerId);
      setShowAssignCustomerDialog(false);
      setUserForCustomerAssignment(null);
      if (onRefresh) {
        onRefresh();
      }
      await fetchPendingUsers();
    } catch (error) {
      // Error is handled by parent
    }
  };

  // 필터링된 사용자 목록
  const filteredUsers = useBackendFiltering 
    ? pendingUsers // 백엔드에서 이미 필터링됨
    : (() => {
        // 클라이언트 사이드 필터링 (Admin 모드)
        const customerFilterValue = effectiveCustomerId === undefined ? 'all' : (effectiveCustomerId === null ? 'none' : effectiveCustomerId);
        return filterUsers(pendingUsers, {
          query: searchQuery,
          customerId: typeof customerFilterValue === 'string' 
            ? parseCustomerId(customerFilterValue)
            : (typeof customerFilterValue === 'number' ? customerFilterValue : undefined),
          status: statusFilter
        });
      })();

  const handleResetFilters = () => {
    if (showCustomerFilter) {
      setSelectedCustomerId(null); // 기본값인 "(미입력)"으로 리셋
    }
    resetPendingFilters();
  };

  // 고객사 필터 변경 핸들러
  const handleCustomerFilterChange = (value: number | null | undefined) => {
    if (showCustomerFilter) {
      setSelectedCustomerId(value);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>승인대기중 / 초대 대기</CardTitle>
        <CardDescription>
          관리자 승인이 필요한 사용자(`pending`)와 초대 메일을 보냈지만 가입을 기다리는 사용자(`invited`)를 함께 보여줍니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 검색 및 필터 */}
        <PendingUsersFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          selectedCustomerId={showCustomerFilter ? selectedCustomerId : effectiveCustomerId}
          onCustomerFilterChange={showCustomerFilter ? handleCustomerFilterChange : () => {}}
          customers={showCustomerFilter ? (customers as Customer[]) : []}
          showCustomerFilter={showCustomerFilter}
          loading={loading}
          onReset={handleResetFilters}
        />
        
        {error && (
          <AlertMessage 
            type="error" 
            message={error} 
            onClose={() => {}} 
          />
        )}
        
        {loading && pendingUsers.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner size="md" message="승인 대기 사용자를 불러오는 중..." />
          </div>
        ) : (
          <PendingUsersTable
            users={filteredUsers}
            page={page}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            showCustomerFilter={showCustomerFilter}
            showCreateCustomer={showCreateCustomer}
            approvingUserId={approvingUserId}
            onEditUser={onEditUser}
            onApproveUser={async (userId) => {
              await onApproveUser(userId);
            }}
            onRejectUser={(user) => {
              setUserToReject(user);
              setConfirmRejectOpen(true);
            }}
            onCancelInvite={(user) => {
              setUserToCancelInvite(user);
              setConfirmCancelInviteOpen(true);
            }}
            onAssignCustomer={(user) => {
              setUserForCustomerAssignment(user);
              setShowAssignCustomerDialog(true);
            }}
            onCreateCustomer={(user) => {
              setUserForCustomerCreation(user);
              // 사용자가 입력한 회사명이 있으면 기본값으로 설정
              // 담당자 정보는 현재 사용자 정보로 기본값 설정
              setNewCustomer({
                customer_code: '',
                alias: '',
                customer_name: user.company_name || '',
                max_users: PLAN_LIMITS.PROFESSIONAL.default,
                service_url: '',
                contract_end_date: '',
                plan_id: 1,
                contact_name: user.name || '',
                contact_email: user.email || '',
                contact_phone: '',
                notes: ''
              });
              setShowCreateCustomerDialog(true);
            }}
            onShowApproveDialog={(user) => {
              setApprovingUser(user);
              setShowApproveDialog(true);
            }}
            fetchPendingUsers={fetchPendingUsers}
            searchQuery={searchQuery}
          />
        )}
        
        {/* 승인 다이얼로그 */}
        <ApproveUserDialog
          open={showApproveDialog}
          onOpenChange={setShowApproveDialog}
          user={approvingUser}
          onApprove={onApproveUser}
          onCreateCustomer={onCreateCustomer}
          showCreateCustomer={showCreateCustomer}
          onSuccess={fetchPendingUsers}
        />
        
        {/* 고객사 생성 다이얼로그 */}
        <CustomerCreateForm
          open={showCreateCustomerDialog}
          newCustomer={newCustomer}
          plans={plans}
          mainDomain={mainDomain}
          loading={customerActions.loading}
          onCustomerChange={setNewCustomer}
          onSubmit={handleCreateCustomer}
          onCancel={() => {
            setShowCreateCustomerDialog(false);
            setUserForCustomerCreation(null);
            setNewCustomer({
              customer_code: '',
              alias: '',
              customer_name: '',
              max_users: PLAN_LIMITS.PROFESSIONAL.default,
              service_url: '',
              contract_end_date: '',
              plan_id: 1,
              contact_name: '',
              contact_email: '',
              contact_phone: '',
              notes: ''
            });
          }}
        />
        
        {/* 고객사 할당 다이얼로그 */}
        <CustomerAssignDialog
          open={showAssignCustomerDialog}
          onOpenChange={setShowAssignCustomerDialog}
          userEmail={userForCustomerAssignment?.email}
          onAssign={handleAssignCustomer}
        />

        {/* 거부 확인 모달 */}
        <ConfirmDialog
          open={confirmRejectOpen}
          title="가입 거부"
          description={`정말로 "${userToReject?.email}" 사용자의 가입을 거부하시겠습니까?\n\n거부된 사용자는 차단목록에 기록됩니다.`}
          confirmText="거부"
          cancelText="취소"
          variant="destructive"
          onConfirm={async () => {
            if (!userToReject) return;
            try {
              await adminAPI.deleteUser(userToReject.id);
              // 상위 컴포넌트의 refreshAllTabs 호출하여 탭 카운트 업데이트 (차단목록 갯수 포함)
              if (onRefresh) {
                await onRefresh(); // await로 완료 대기
              }
              await fetchPendingUsers();
              setConfirmRejectOpen(false);
              setUserToReject(null);
            } catch (error: any) {
              setAlertTitle('오류');
              setAlertMessage(handleAPIError(error, '사용자 거부에 실패했습니다.'));
              setAlertOpen(true);
              setConfirmRejectOpen(false);
              setUserToReject(null);
            }
          }}
          onCancel={() => {
            setConfirmRejectOpen(false);
            setUserToReject(null);
          }}
        />

        {/* 초대 취소 확인 모달 */}
        <ConfirmDialog
          open={confirmCancelInviteOpen}
          title="초대 취소"
          description={`정말로 "${userToCancelInvite?.email}" 사용자의 초대를 취소하시겠습니까?`}
          confirmText="취소"
          cancelText="닫기"
          variant="destructive"
          onConfirm={async () => {
            if (!userToCancelInvite) return;
            try {
              // 초대 취소 = 사용자 삭제(soft delete)
              await adminAPI.deleteUser(userToCancelInvite.id);
              if (onRefresh) {
                onRefresh();
              }
              await fetchPendingUsers();
              setConfirmCancelInviteOpen(false);
              setUserToCancelInvite(null);
            } catch (error: any) {
              setAlertTitle('오류');
              setAlertMessage(handleAPIError(error, '초대 취소에 실패했습니다.'));
              setAlertOpen(true);
              setConfirmCancelInviteOpen(false);
              setUserToCancelInvite(null);
            }
          }}
          onCancel={() => {
            setConfirmCancelInviteOpen(false);
            setUserToCancelInvite(null);
          }}
        />

        {/* 알림 모달 */}
        <AlertDialog
          open={alertOpen}
          title={alertTitle}
          message={alertMessage}
          confirmText="확인"
          variant={alertTitle === '오류' ? 'destructive' : 'default'}
          onConfirm={() => setAlertOpen(false)}
        />
      </CardContent>
    </Card>
  );
};

export default PendingUsersTab;
